/**
 * FormFlu Dashboard Home Page
 * 
 * The main dashboard overview with real-time metrics, charts, and activity feed.
 * This is the first page users see after logging in.
 * 
 * HIPAA COMPLIANCE NOTES:
 * - No PHI displayed on dashboard
 * - Only aggregate metrics and metadata
 * - Activity feed shows form names only, not submission content
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DashboardLayout } from '../../components/dashboard';
import { useUserDisplay } from '../../contexts';
import { analyticsApi, type GlobalAnalyticsOverview, type RecentActivity, ApiClientError } from '../../lib/api';
import './DashboardHomePage.css';

export function DashboardHomePage() {
  const navigate = useNavigate();
  const userDisplay = useUserDisplay();
  const [overview, setOverview] = useState<GlobalAnalyticsOverview | null>(null);
  const [activity, setActivity] = useState<RecentActivity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const [overviewData, activityData] = await Promise.all([
          analyticsApi.getGlobalOverview(),
          analyticsApi.getRecentActivity(10),
        ]);
        
        setOverview(overviewData);
        setActivity(activityData);
      } catch (err) {
        const message = err instanceof ApiClientError
          ? err.error.message
          : 'Failed to load dashboard data';
        setError(message);
        
        // Set default data for demo
        setOverview({
          period: { start_date: '', end_date: '' },
          metrics: {
            total_forms: 0,
            active_forms: 0,
            total_submissions: 0,
            submissions_today: 0,
            conversion_rate: 0,
          },
          submission_trend: [],
          top_forms: [],
        });
        setActivity({
          recent_submissions: [],
          recent_forms: [],
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <DashboardLayout>
      <div className="dashboard-home">
        {/* Welcome Header */}
        <header className="dashboard-welcome">
          <div className="welcome-text">
            <h1>{getGreeting()}, {userDisplay?.firstName || 'there'}! 👋</h1>
            <p>Here's what's happening with your forms today.</p>
          </div>
          <div className="welcome-actions">
            <button className="btn btn-primary" onClick={() => navigate('/dashboard/forms')}>
              <span className="btn-icon">+</span>
              Create Form
            </button>
          </div>
        </header>

        {/* Error Banner */}
        {error && (
          <div className="dashboard-error">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>Loading your dashboard...</p>
          </div>
        ) : (
          <>
            {/* Metrics Cards */}
            <section className="metrics-section">
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-icon forms">📝</div>
                  <div className="metric-content">
                    <span className="metric-value">{overview?.metrics.total_forms || 0}</span>
                    <span className="metric-label">Total Forms</span>
                  </div>
                  <div className="metric-badge">
                    <span className="badge-active">{overview?.metrics.active_forms || 0} active</span>
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-icon submissions">📬</div>
                  <div className="metric-content">
                    <span className="metric-value">{overview?.metrics.total_submissions || 0}</span>
                    <span className="metric-label">Total Submissions</span>
                  </div>
                  <div className="metric-trend positive">
                    <span>+{overview?.metrics.submissions_today || 0} today</span>
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-icon today">📊</div>
                  <div className="metric-content">
                    <span className="metric-value">{overview?.metrics.submissions_today || 0}</span>
                    <span className="metric-label">Today's Submissions</span>
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-icon conversion">🎯</div>
                  <div className="metric-content">
                    <span className="metric-value">{overview?.metrics.conversion_rate || 0}%</span>
                    <span className="metric-label">Conversion Rate</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Charts & Activity Row */}
            <section className="dashboard-grid">
              {/* Submission Trend Chart */}
              <div className="dashboard-card chart-card">
                <div className="card-header">
                  <h3>Submission Trends</h3>
                  <span className="card-subtitle">Last 30 days</span>
                </div>
                <div className="chart-container">
                  {overview?.submission_trend && overview.submission_trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={overview.submission_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          labelFormatter={(value) => new Date(value).toLocaleDateString()}
                          contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="submissions" 
                          stroke="#1FB6A6" 
                          strokeWidth={2}
                          dot={{ fill: '#1FB6A6', strokeWidth: 2 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="chart-empty">
                      <span className="empty-icon">📈</span>
                      <p>No submission data yet</p>
                      <span className="empty-hint">Create and publish a form to start collecting data</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Top Forms */}
              <div className="dashboard-card">
                <div className="card-header">
                  <h3>Top Performing Forms</h3>
                  <Link to="/dashboard/analytics" className="card-link">View all →</Link>
                </div>
                <div className="top-forms-list">
                  {overview?.top_forms && overview.top_forms.length > 0 ? (
                    overview.top_forms.map((form: { form_id: string; form_name: string; submission_count: number }, index: number) => (
                      <div key={form.form_id} className="top-form-item">
                        <span className="form-rank">#{index + 1}</span>
                        <div className="form-info">
                          <span className="form-name">{form.form_name}</span>
                          <span className="form-submissions">{form.submission_count} submissions</span>
                        </div>
                        <Link to={`/dashboard/forms/${form.form_id}/analytics`} className="form-link">
                          View →
                        </Link>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state-inline">
                      <span className="empty-icon">🏆</span>
                      <p>No forms yet</p>
                      <button className="btn btn-sm btn-secondary" onClick={() => navigate('/dashboard/forms')}>
                        Create your first form
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Activity & Quick Actions Row */}
            <section className="dashboard-grid">
              {/* Recent Activity */}
              <div className="dashboard-card activity-card">
                <div className="card-header">
                  <h3>Recent Activity</h3>
                  <Link to="/dashboard/submissions" className="card-link">View all →</Link>
                </div>
                <div className="activity-list">
                  {activity?.recent_submissions && activity.recent_submissions.length > 0 ? (
                    activity.recent_submissions.slice(0, 7).map((submission: { id: string; form_id: string; form_name: string; submitted_at: string; status: string }) => (
                      <div key={submission.id} className="activity-item">
                        <div className="activity-icon">
                          <span className={`status-dot status-${submission.status}`}></span>
                        </div>
                        <div className="activity-content">
                          <span className="activity-text">
                            New submission on <strong>{submission.form_name}</strong>
                          </span>
                          <span className="activity-time">{formatRelativeTime(submission.submitted_at)}</span>
                        </div>
                        <Link 
                          to={`/dashboard/forms/${submission.form_id}/submissions`} 
                          className="activity-link"
                        >
                          View
                        </Link>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state-inline">
                      <span className="empty-icon">📥</span>
                      <p>No submissions yet</p>
                      <span className="empty-hint">Submissions will appear here as they come in</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="dashboard-card">
                <div className="card-header">
                  <h3>Quick Actions</h3>
                </div>
                <div className="quick-actions">
                  <button className="quick-action" onClick={() => navigate('/dashboard/forms')}>
                    <span className="action-icon">📝</span>
                    <span className="action-text">Create New Form</span>
                  </button>
                  <button className="quick-action" onClick={() => navigate('/dashboard/submissions')}>
                    <span className="action-icon">📬</span>
                    <span className="action-text">View Submissions</span>
                  </button>
                  <button className="quick-action" onClick={() => navigate('/dashboard/analytics')}>
                    <span className="action-icon">📊</span>
                    <span className="action-text">View Analytics</span>
                  </button>
                  <button className="quick-action" onClick={() => navigate('/dashboard/team')}>
                    <span className="action-icon">👥</span>
                    <span className="action-text">Manage Team</span>
                  </button>
                  <button className="quick-action" onClick={() => navigate('/dashboard/settings')}>
                    <span className="action-icon">⚙️</span>
                    <span className="action-text">Settings</span>
                  </button>
                  <button className="quick-action" onClick={() => navigate('/dashboard/help')}>
                    <span className="action-icon">❓</span>
                    <span className="action-text">Help & Support</span>
                  </button>
                </div>
              </div>
            </section>

            {/* Getting Started (show if no forms) */}
            {overview?.metrics.total_forms === 0 && (
              <section className="getting-started-section">
                <div className="dashboard-card getting-started-card">
                  <div className="getting-started-header">
                    <span className="getting-started-icon">🚀</span>
                    <div>
                      <h3>Welcome to FormFlu!</h3>
                      <p>Let's get you started with your first form</p>
                    </div>
                  </div>
                  <div className="getting-started-steps">
                    <div className="step">
                      <span className="step-number">1</span>
                      <div className="step-content">
                        <h4>Create a form</h4>
                        <p>Build your form using our drag-and-drop builder</p>
                      </div>
                    </div>
                    <div className="step">
                      <span className="step-number">2</span>
                      <div className="step-content">
                        <h4>Add form fields</h4>
                        <p>Add text fields, dropdowns, checkboxes, and more</p>
                      </div>
                    </div>
                    <div className="step">
                      <span className="step-number">3</span>
                      <div className="step-content">
                        <h4>Publish & embed</h4>
                        <p>Get embed code and add your form to any website</p>
                      </div>
                    </div>
                  </div>
                  <div className="getting-started-action">
                    <button className="btn btn-primary btn-lg" onClick={() => navigate('/dashboard/forms')}>
                      Create Your First Form →
                    </button>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default DashboardHomePage;
